
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateRangePicker } from "@/components/DateRangePicker";
import { FileText, Hash, Flag, Trello } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DateRange } from "react-day-picker";
import { formatProjectType } from "@/utils/formatters";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";

interface ProjectDetailsCardProps {
  project: {
    id: number;
    code: string | null;
    type: string | null;
    start_date: string | null;
    end_date: string | null;
  };
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  refetch: () => void;
  boardId: string | null;
}

export function ProjectDetailsCard({ 
  project, 
  dateRange, 
  onDateRangeChange,
  refetch,
  boardId
}: ProjectDetailsCardProps) {
  const { toast } = useToast();
  const [editingField, setEditingField] = useState<{
    field: string;
    value: string;
  } | null>(null);
  const [isCreatingBoard, setIsCreatingBoard] = useState(false);

  const handleUpdateField = async (field: string, value: string) => {
    const { error } = await supabase
      .from('projects')
      .update({ [field]: value })
      .eq('id', project.id);

    if (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to update ${field}`,
      });
      return;
    }

    toast({
      title: "Success",
      description: `${field} updated successfully`,
    });
    setEditingField(null);
    refetch();
  };

  const handleCreateBoard = async () => {
    setIsCreatingBoard(true);
    
    try {
      const { data, error } = await supabase
        .from('boards')
        .insert([{ project_id: project.id }])
        .select();
      
      if (error) throw error;
      
      if (data && data.length > 0) {
        // Create a backlog sprint for the new board
        await createBacklogSprint(data[0].id);
        
        toast({
          title: "Board created",
          description: "Board has been created successfully",
        });
        refetch();
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create board",
      });
    } finally {
      setIsCreatingBoard(false);
    }
  };

  // New helper function to create a backlog sprint
  const createBacklogSprint = async (boardId: string) => {
    try {
      // Create a backlog sprint with infinite dates (far in the past to far in the future)
      const pastDate = new Date(2000, 0, 1); // January 1, 2000
      const futureDate = new Date(2100, 11, 31); // December 31, 2100
      
      const { data, error } = await supabase
        .from('sprints')
        .insert([
          { 
            title: 'Backlog',
            start_date: pastDate.toISOString().split('T')[0],
            end_date: futureDate.toISOString().split('T')[0],
            status: 'backlog',
            board_id: boardId
          }
        ])
        .select();
        
      if (error) throw error;
      
      console.log('Backlog sprint created successfully from project card:', data);
      return data;
    } catch (error) {
      console.error('Error creating backlog sprint:', error);
    }
  };

  const EditableField = ({ 
    icon: Icon, 
    label, 
    value, 
    field,
    type = 'text'
  }: { 
    icon: React.ElementType; 
    label: string; 
    value?: string | null;
    field: string;
    type?: 'text' | 'select';
  }) => {
    const isEditing = editingField?.field === field;
    const [editValue, setEditValue] = useState(value || '');

    const projectTypes = [
      { value: 'fixed_price', label: 'Fixed Price' },
      { value: 'time_materials', label: 'Time and Materials' }
    ];

    return (
      <div className="flex items-start gap-2">
        <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
        <div className="flex-1">
          <p className="font-medium">{label}</p>
          {isEditing ? (
            <div className="flex gap-2 mt-1">
              {type === 'select' ? (
                <Select
                  value={editValue}
                  onValueChange={(value) => {
                    setEditValue(value);
                    handleUpdateField(field, value);
                  }}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {projectTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <Input 
                    value={editValue} 
                    onChange={(e) => setEditValue(e.target.value)}
                    className="h-8"
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleUpdateField(field, editValue);
                      if (e.key === "Escape") setEditingField(null);
                    }}
                    autoFocus
                  />
                  <Button size="sm" onClick={() => handleUpdateField(field, editValue)}>
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingField(null)}>
                    Cancel
                  </Button>
                </>
              )}
            </div>
          ) : (
            <div 
              className="cursor-pointer hover:bg-accent hover:text-accent-foreground p-1 rounded-md -ml-1"
              onClick={() => {
                setEditingField({ field, value: value || '' });
                setEditValue(value || '');
              }}
            >
              {value || (
                <div className="flex items-center gap-1 text-muted-foreground">
                  <span className="text-sm">Click to add {label.toLowerCase()}</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Project Details
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <EditableField 
          icon={Hash} 
          label="Project Code" 
          value={project.code}
          field="code"
        />
        <EditableField 
          icon={Flag} 
          label="Type" 
          value={formatProjectType(project.type)}
          field="type"
          type="select"
        />
        <div className="space-y-2">
          <Label className="font-medium">Project Dates</Label>
          <DateRangePicker
            date={dateRange}
            onDateChange={onDateRangeChange}
          />
        </div>
        
        <div className="pt-2">
          <div className="flex items-start gap-2">
            <Trello className="h-5 w-5 mt-0.5 text-muted-foreground" />
            <div className="flex-1">
              <p className="font-medium">Task Board</p>
              {boardId ? (
                <Link 
                  to={`/board?boardId=${boardId}`} 
                  className="inline-flex items-center gap-1.5 mt-1 text-blue-600 hover:underline"
                >
                  View project board <span className="text-xs">→</span>
                </Link>
              ) : (
                <Button 
                  onClick={handleCreateBoard} 
                  disabled={isCreatingBoard} 
                  size="sm" 
                  className="mt-1"
                >
                  {isCreatingBoard ? "Creating..." : "Create Board"}
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
